import { Button } from "@/shared/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { useEffect, useMemo, useState } from "react";
import { validateKwargsByCheckType } from "../hooks/useAnomalyDetectionRules";
import type { AnomalyDetectionCheckType, AnomalyDetectionRule } from "../types/preferences_types";

export function AnomalyDetectionRuleDialog({
    open,
    onOpenChange,
    mode,
    initial,
    channels,
    checkTypes,
    errorMsg,
    onClearError,
    onSubmit,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: "create" | "edit";
    initial?: AnomalyDetectionRule;
    channels: string[];
    checkTypes: readonly AnomalyDetectionCheckType[];
    errorMsg?: string;
    onClearError?: () => void;
    onSubmit: (payload: {
        channel: string;
        reportType: string;
        checkType: AnomalyDetectionCheckType;
        kwargsByCheckType: Record<string, unknown>;
    }) => void;
}) {
    const title = useMemo(() => (mode === "create" ? "이상 탐지 규칙 추가" : "이상 탐지 규칙 수정"), [mode]);

    const [channel, setChannel] = useState<string>(channels[0] ?? "");
    const [reportType, setReportType] = useState<string>("*");
    const [checkType, setCheckType] = useState<AnomalyDetectionCheckType>(checkTypes[0] ?? "missing_data");
    const [kwargsRaw, setKwargsRaw] = useState<string>("{}");

    useEffect(() => {
        if (!open) return;
        onClearError?.();

        if (mode === "edit" && initial) {
            setChannel(initial.channel);
            setReportType(initial.reportType);
            setCheckType(initial.checkType);
            setKwargsRaw(JSON.stringify(initial.kwargsByCheckType));
            return;
        }

        setChannel(channels[0] ?? "");
        setReportType("*");
        setCheckType(checkTypes[0] ?? "missing_data");
        setKwargsRaw("{}");
    }, [open, mode, initial, channels, checkTypes, onClearError]);

    const canSubmit = channel.trim() && reportType.trim() && checkType && kwargsRaw.trim();

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <div className="text-sm font-medium">채널</div>
                        <Select
                            value={channel}
                            onValueChange={(v) => {
                                onClearError?.();
                                setChannel(v);
                            }}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {channels.map((c) => (
                                    <SelectItem key={c} value={c}>
                                        {c}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <div className="text-sm font-medium">리포트 타입</div>
                        <Input
                            value={reportType}
                            onChange={(e) => {
                                onClearError?.();
                                setReportType(e.target.value);
                            }}
                            maxLength={64}
                            placeholder="예: campaign, adgroup, * 또는 -"
                        />
                        <div className="text-xs text-muted-foreground">
                            리포트 타입명 또는 *, - (*: 모든 리포트, -: 보고서 단위가 아닌 수집 단위)
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="text-sm font-medium">체크 타입</div>
                        <Select
                            value={checkType}
                            onValueChange={(v) => {
                                onClearError?.();
                                setCheckType(v as AnomalyDetectionCheckType);
                            }}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {checkTypes.map((t) => (
                                    <SelectItem key={t} value={t}>
                                        {t}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <div className="text-sm font-medium">체크 타입 별 인자(JSON)</div>
                        <Input
                            value={kwargsRaw}
                            onChange={(e) => {
                                onClearError?.();
                                setKwargsRaw(e.target.value);
                            }}
                            placeholder='예: {"percentage_threshold": 25}'
                        />
                    </div>

                    {errorMsg && (
                        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                            {errorMsg}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>
                        취소
                    </Button>
                    <Button
                        disabled={!canSubmit}
                        onClick={() => {
                            const v = validateKwargsByCheckType(checkType, kwargsRaw);
                            if (!v.ok) {
                                // 페이지에서 setErrorMsg로 처리하는 패턴을 쓰기 위해,
                                // 여기서는 onSubmit 호출 전에 페이지에서 검사해도 됨.
                                onSubmit({ channel, reportType: reportType.trim(), checkType, kwargsByCheckType: {} });
                                return;
                            }
                            onSubmit({
                                channel,
                                reportType: reportType.trim(),
                                checkType,
                                kwargsByCheckType: v.value,
                            });
                        }}
                    >
                        {mode === "create" ? "추가" : "수정"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}